import { useId } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  etiqueta: string;
  activo: boolean;
  /** Sin permiso de edición se sigue viendo el valor, pero no se puede cambiar. */
  editable: boolean;
  ocupado: boolean;
  onCambio: (valor: boolean) => void;
  /**
   * Depende del interruptor de arriba: se dibuja sangrado para que se lea como
   * lo que es, un matiz de la decisión anterior y no una decisión aparte.
   */
  anidado?: boolean;
  /** Cuando «Permitido / No permitido» no es lo que el interruptor significa. */
  textoActivo?: string;
  textoInactivo?: string;
}

/**
 * Un permiso del documento que se lee y se cambia en el mismo lugar.
 *
 * Estuvo un tiempo sólo como dato: la pantalla decía «No permitido» y no había
 * dónde cambiarlo. Un valor que se muestra y no se puede tocar en ninguna parte
 * de la aplicación es un valor que en la práctica no existe.
 */
export function Interruptor({
  etiqueta,
  activo,
  editable,
  ocupado,
  onCambio,
  anidado = false,
  textoActivo,
  textoInactivo,
}: Props) {
  const { t } = useTranslation();
  const id = useId();

  const clase = `dato${anidado ? ' dato--anidado' : ''}`;
  const leyenda = activo
    ? (textoActivo ?? t('documents.allowed'))
    : (textoInactivo ?? t('documents.notAllowed'));

  if (!editable) {
    return (
      <div className={clase}>
        <span className="dato__etiqueta">{etiqueta}</span>
        <span className="dato__valor">{leyenda}</span>
      </div>
    );
  }

  return (
    <div className={clase}>
      <label className="dato__etiqueta" htmlFor={id}>
        {etiqueta}
      </label>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={activo}
        disabled={ocupado}
        className={`interruptor${activo ? ' interruptor--activo' : ''}`}
        onClick={() => onCambio(!activo)}
      >
        <span className="interruptor__perilla" aria-hidden="true" />
        <span className="interruptor__texto">{leyenda}</span>
      </button>
    </div>
  );
}
